<template>
    <div class="service-basket flow__panel flow__container-fluid flow__panel--positioned-buttons">
        <div>
            <section>
                <h2>Financial Advice</h2>
                <p>Our partners at Knight Frank Finance can give you expert, independent advice on all sorts of financial products. Choose the options you’re interested in, and we’ll send them a no-obligation enquiry on your behalf.</p>
                <p>Add the services you are interested in to your basket.</p>
            </section>
            <error-handler />
            <section>
                <div v-for="item in allServiceProducts"
                     :key="item.serviceTypeId"
                     class="service-basket__service-item">
                    <service-product-item :name="item.name"
                                          :cost="item.cost"
                                          :in-basket="item.inBasket"
                                          :disabled="!item.canAdd"
                                          @add="addServiceProductAsync(item)"
                                          @remove="removeServiceProductAsync(item)">
                        {{ item.description }}
                    </service-product-item>
                </div>
            </section>
            <section class="service-basket__terms">
                <p><strong>Mortgage Advice:</strong> The choice of interest rate and product terms will depend on your circumstances and the amount of the mortgage. Before you make a mortgage application, we will carry out a full review to establish your needs and preferences. If you meet the criteria, we will give advice and make a recommendation to you. We do charge a fee for mortgage advice. All mortgages are subject to status. Please note that all products show an indicative rate only and may not be suitable for you. You must be 18 or over.</p>
                <p>Your home may be repossessed if you do not keep up with mortgage payments.</p>
                <p>Knight Frank Finance LLP is authorised and regulated by the Financial Conduct Authority under Financial Services Register number 123456.</p>
                <p><strong>Disclosure:</strong> if you seek paid-for advice from our partner, Ecofurb will receive a commission to help fund their mission to deliver free online advice and a retrofit plan to every home.</p>
            </section>
        </div>
        <aside>
            <basket :service-basket="serviceBasket" @delete="removeServiceProductAsync" />
        </aside>
    </div>
</template>

<script>

import Vuex from 'vuex'

import ProgressManager from '@/progress-manager'
import ServiceBasketManager from '@/service-basket-manager'
import ErrorHandler from '@/components/app-core/error-handler'
import Basket from '@/components/service-basket/basket'
import ServiceProductItem from '@/components/service-basket/service-product-item'
import ServiceProduct from '@/models/service-product'
import { MUTATIONS } from '@/store/mutations'

export default {
    components: {
        Basket,
        ErrorHandler,
        ServiceProductItem,
    },

    data() {
        return {
            /**
             * @type {Array<ServiceProduct>}
             */
            allServiceProducts: [
                new ServiceProduct(5, 'Mortgages', null, 'If you’re looking to purchase a property, or don’t have a mortgage on your property at the moment, you may be able to take out a new mortgage. The amount you can borrow will depend on your income and expenditure.'),
                new ServiceProduct(6, 'Green Mortgages', null, 'Green mortgages offer preferential deals based on a property’s energy performance certificate (EPC). These products are available to homebuyers and, in some instances, people who want to remortgage.'),
                new ServiceProduct(7, 'Remortgage', null, 'You may be able to raise the funds you need by remortgaging to a new lender. You will need to consider the costs involved.'),
                new ServiceProduct(8, 'Further Advance', null, 'Most mortgage lenders will allow you to increase your current borrowing, providing you have enough income to support the increase in costs.'),
                new ServiceProduct(9, 'Second Charge Loan', null, 'If your existing lender can’t help but you don’t want to move your mortgage, you may want to consider a second charge loan secured on your home. This is similar to a mortgage but will be with a different lender and is likely to have different terms. Speak to a mortgage advisor about what a second charge might look like.'),
                new ServiceProduct(10, 'Over 55s Financing', null, 'There is a growing number of mortgage options available for homeowners aged over 55, including Retirement Interest Only (RIO) mortgages and Lifetime mortgages.'),
                new ServiceProduct(11, 'Other Independent Financial Advice', null, 'General Independent Financial Advice to support your retrofit goals.'),
            ],
        }
    },

    async mounted() {
        ProgressManager.clearVisitedRoute(this.$router.currentRoute.name)
        await ServiceBasketManager.loadBasketAsync()
        this._updateStateFromBasket()
    },

    computed: {
        ...Vuex.mapState({
            /**
             * @param {VuexState} stateStore
             * @returns {ServiceBasket}
             */
            serviceBasket: stateStore => stateStore.serviceBasket.basket,
        }),
    },

    methods: {
        /**
         * Update changes to the reactive state based on the state of the basket.
         */
        _updateStateFromBasket() {
            if (!this.serviceBasket) return

            // reset the state of all service products
            this.allServiceProducts = this.allServiceProducts.map(item => ({
                ...item,
                canAdd: true,
                inBasket: false,
            }))

            // update the state of all the service products based on what is in the service basket
            this.serviceBasket.items.forEach(item => {
                const serviceProduct = this.allServiceProducts.find(({ serviceTypeId }) => serviceTypeId === item.serviceTypeId)

                if (serviceProduct)
                    serviceProduct.inBasket = true
            })
        },

        /**
         * Add a service item to the basket.
         * @param {ServiceProduct} item
         */
        async addServiceProductAsync(item) {
            this.$store.commit(MUTATIONS.clearAllErrors)
            await ServiceBasketManager.addItemToBasketAsync(item)
            this._updateStateFromBasket()
        },

        /**
         * Remove a service item from the basket.
         * @param {ServiceProduct} item
         */
        async removeServiceProductAsync(item) {
            this.$store.commit(MUTATIONS.clearAllErrors)
            await ServiceBasketManager.removeItemFromBasketAsync(item)
            this._updateStateFromBasket()
        },
    },
}

</script>

<style lang="scss">
// NOTE all of the styles are for this page defined in an unscoped style block in home-energy-plan.vue
// which defines the `.service-basket` classes and all derived classes
</style>
